import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getStorageItem, setStorageItem } from 'locale';

import { Hint } from 'models/Hint';

export type IncludeBirthDate = {
  value?: boolean;
};
export type ValidityDate = {
  value?: string;
};

export type MandateState = {
  hints: Hint[];
  persistedFormValues: {
    includeBirthDate: IncludeBirthDate;
    validityDate: ValidityDate;
  };
};

const defaultStorageValue = {
  includeBirthDate: { value: undefined },
  validityDate: { value: undefined }
};

const STORAGE_KEY = 'mandate_persisted_form_values';

const getStoredFormValues = (): MandateState['persistedFormValues'] => {
  try {
    const stored = getStorageItem(STORAGE_KEY);
    return stored ? JSON.parse(stored as string) : defaultStorageValue;
  } catch (error) {
    console.error('Failed to parse stored form values:', error);
    return defaultStorageValue;
  }
};

const initialState: MandateState = {
  hints: [],
  persistedFormValues: getStoredFormValues()
};

export const mandateSlice = createSlice({
  name: 'mandate',
  initialState,
  reducers: {
    updateHints(state, action: PayloadAction<Hint>) {
      const index = state.hints.findIndex(item => item.creditor === action.payload.creditor);

      if (index !== -1) {
        state.hints[index] = action.payload;
      } else {
        state.hints.push(action.payload);
      }

      return state;
    },
    setIncludeBirthDate(state, action: PayloadAction<IncludeBirthDate>) {
      state.persistedFormValues = {
        ...state.persistedFormValues,
        includeBirthDate: action.payload
      };
      setStorageItem(STORAGE_KEY, JSON.stringify(state.persistedFormValues));
    },
    setValidityDate(state, action: PayloadAction<ValidityDate>) {
      state.persistedFormValues = {
        ...state.persistedFormValues,
        validityDate: action.payload
      };
      setStorageItem(STORAGE_KEY, JSON.stringify(state.persistedFormValues));
    },
    clearPersistedFormValues(state) {
      state.persistedFormValues = defaultStorageValue;
      setStorageItem(STORAGE_KEY, JSON.stringify(defaultStorageValue));
    }
  },
  selectors: {
    getHints: state => state.hints,
    getIncludeBirthDate: (state: MandateState) => {
      return state.persistedFormValues.includeBirthDate;
    },
    getValidityDate: (state: MandateState) => {
      return state.persistedFormValues.validityDate;
    }
  }
});

export const { updateHints, clearPersistedFormValues, setIncludeBirthDate, setValidityDate } =
  mandateSlice.actions;
export const { getHints, getIncludeBirthDate, getValidityDate } = mandateSlice.selectors;
